import React, { useState, useMemo } from "react"
import { useHistory } from "react-router-dom"
import { useFormik } from "formik"
import { string, object } from "yup"
import { useTranslation } from "react-i18next"
import type { TFunction } from "i18next"
import type { ConfirmationState } from "../../../../constants/types"

import Progressbar from "../../../../components/progressbar"
import Breadcrumb from "../../../../components/breadcrumb"
import Tooltip from "../../../../components/tooltip"
import ConfirmationModal from "../../../../components/confirmation"
import Emulator from "../../../../components/emulator"
import { twClassNames } from "../../../../helpers/classNames"
import { ISODateNow } from "../../../../helpers/time"
import { withHttp } from "../../../../helpers/withHttp"
import { getLocaleValue } from "../../../../helpers/getLocaleValue"
import { useCampaigns, useCreateCampaign } from "../../../../hooks/useCampaigns"
import { useActivity } from "../../../../hooks/useActivities"
import { useSelectedProject } from "../../../../contexts/SelectedProjectContext"

const PHASES = 3
const breadcrumbs = [
  { name: "activities.campaigns.create.breadcrumb.campaigns", url: "/activities/campaigns" },
  { name: "activities.campaigns.create.breadcrumb.create" }
]

const validationSchema = (phase: number, t: TFunction) => {
  if (phase === 1) {
    return object().shape(
      {
        shortTitleFi: string()
          .when(["shortTitleEn"], {
            is: (shortTitleEn: string) => !shortTitleEn,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(40, t("common.validation.invalidMaxLength")),
        shortTitleEn: string()
          .when(["shortTitleFi"], {
            is: (shortTitleFi: string) => !shortTitleFi,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(40, t("common.validation.invalidMaxLength")),
        shortDescriptionFi: string()
          .when(["shortDescriptionEn"], {
            is: (shortDescriptionEn: string) => !shortDescriptionEn,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(120, t("common.validation.invalidMaxLength")),
        shortDescriptionEn: string()
          .when(["shortDescriptionFi"], {
            is: (shortDescriptionFi: string) => !shortDescriptionFi,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(120, t("common.validation.invalidMaxLength"))
      },
      [
        ["shortTitleFi", "shortTitleEn"],
        ["shortDescriptionFi", "shortDescriptionEn"]
      ]
    )
  } else if (phase === 2) {
    return object().shape(
      {
        titleFi: string()
          .when(["titleEn"], {
            is: (titleEn: string) => !titleEn,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(120, t("common.validation.invalidMaxLength")),
        titleEn: string()
          .when(["titleFi"], {
            is: (titleFi: string) => !titleFi,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(120, t("common.validation.invalidMaxLength")),
        descriptionFi: string()
          .when(["descriptionEn"], {
            is: (descriptionEn: string) => !descriptionEn,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(300, t("common.validation.invalidMaxLength")),
        descriptionEn: string()
          .when(["descriptionFi"], {
            is: (descriptionFi: string) => !descriptionFi,
            then: () => string().required(t("common.validation.eitherRequired"))
          })
          .max(300, t("common.validation.invalidMaxLength"))
      },
      [
        ["titleFi", "titleEn"],
        ["descriptionFi", "descriptionEn"]
      ]
    )
  } else if (phase === 3) {
    return object().shape({})
  }
}

interface FormProps {
  t: TFunction
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: any
  isLoading: boolean
  isValidCLass: (field: string) => string | false
  onPrevious: () => void
  onNext: () => void
}

interface PreviewProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  data: any
  t: TFunction
  phase: number
}

const Form1 = ({ t, formik, isLoading, isValidCLass, onPrevious, onNext }: FormProps) => (
  <div className="tw-flex tw-flex-col tw-h-full">
    {/* Header */}
    <div className="tw-min-w-[300px] tw-w-full tw-mb-4 tw-border-b">
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-text-lg tw-mb-4">{t("activities.campaigns.create.form.phase1.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("activities.campaigns.create.form.phase1.info")}</p>
      </div>
    </div>
    {/* Content */}
    <div className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
      {/* Short title FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="shortTitleFi" className="tw-form-label">
          {t("activities.campaigns.create.form.shortTitleFi")}
          <Tooltip message="activities.campaigns.create.tooltips.shortTitleFi" displayLabel />
        </label>
        <input
          type="text"
          id="shortTitleFi"
          name="shortTitleFi"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.shortTitleFi}
          className={twClassNames(isValidCLass("shortTitleFi"), "tw-form-control tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.shortTitleFi.length}/40</span>
        <div className="tw-invalid-tooltip">{formik.errors.shortTitleFi}</div>
      </div>
      {/* Short title EN */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="shortTitleEn" className="tw-form-label">
          {t("activities.campaigns.create.form.shortTitleEn")}
        </label>
        <input
          type="text"
          id="shortTitleEn"
          name="shortTitleEn"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.shortTitleEn}
          className={twClassNames(isValidCLass("shortTitleEn"), "tw-form-control tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.shortTitleEn.length}/40</span>
        <div className="tw-invalid-tooltip">{formik.errors.shortTitleEn}</div>
      </div>
      {/* Short description FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="shortDescriptionFi" className="tw-form-label">
          {t("activities.campaigns.create.form.shortDescriptionFi")}
        </label>
        <textarea
          id="shortDescriptionFi"
          name="shortDescriptionFi"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.shortDescriptionFi}
          className={twClassNames(isValidCLass("shortDescriptionFi"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.shortDescriptionFi.length}/120</span>
        <div className="tw-invalid-tooltip">{formik.errors.shortDescriptionFi}</div>
      </div>
      {/* Short description EN */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="shortDescriptionEn" className="tw-form-label">
          {t("activities.campaigns.create.form.shortDescriptionEn")}
        </label>
        <textarea
          id="shortDescriptionEn"
          name="shortDescriptionEn"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.shortDescriptionEn}
          className={twClassNames(isValidCLass("shortDescriptionEn"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.shortDescriptionEn.length}/120</span>
        <div className="tw-invalid-tooltip">{formik.errors.shortDescriptionEn}</div>
      </div>
    </div>
    {/* Buttons */}
    <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
      <button
        type="button"
        onClick={onPrevious}
        className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.cancel")}
      </button>
      <button
        type="button"
        onClick={onNext}
        className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.next")}
      </button>
    </div>
  </div>
)

const Form2 = ({ t, formik, isLoading, isValidCLass, onPrevious, onNext }: FormProps) => (
  <div className="tw-flex tw-flex-col tw-h-full">
    {/* Header */}
    <div className="tw-min-w-[300px] tw-w-full tw-mb-4 tw-border-b">
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-text-lg tw-mb-4">{t("activities.campaigns.create.form.phase2.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("activities.campaigns.create.form.phase2.info")}</p>
      </div>
    </div>
    {/* Content */}
    <div className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
      {/* Title FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="titleFi" className="tw-form-label">
          {t("activities.campaigns.create.form.titleFi")}
          <Tooltip message="activities.campaigns.create.tooltips.titleFi" displayLabel />
        </label>
        <textarea
          id="titleFi"
          name="titleFi"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.titleFi}
          className={twClassNames(isValidCLass("titleFi"), "tw-form-control tw-min-h-[55px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.titleFi.length}/120</span>
        <div className="tw-invalid-tooltip">{formik.errors.titleFi}</div>
      </div>
      {/* Title EN */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="titleEn" className="tw-form-label">
          {t("activities.campaigns.create.form.titleEn")}
        </label>
        <textarea
          id="titleEn"
          name="titleEn"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.titleEn}
          className={twClassNames(isValidCLass("titleEn"), "tw-form-control tw-min-h-[55px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.titleEn.length}/120</span>
        <div className="tw-invalid-tooltip">{formik.errors.titleEn}</div>
      </div>
      {/* Description FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="descriptionFi" className="tw-form-label">
          {t("activities.campaigns.create.form.descriptionFi")}
        </label>
        <textarea
          id="descriptionFi"
          name="descriptionFi"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.descriptionFi}
          className={twClassNames(isValidCLass("descriptionFi"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.descriptionFi.length}/300</span>
        <div className="tw-invalid-tooltip">{formik.errors.descriptionFi}</div>
      </div>
      {/* Description EN */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="descriptionEn" className="tw-form-label">
          {t("activities.campaigns.create.form.descriptionEn")}
        </label>
        <textarea
          id="descriptionEn"
          name="descriptionEn"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.descriptionEn}
          className={twClassNames(isValidCLass("descriptionEn"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
          spellCheck="false"
        />
        <span className="tw-form-counter">{formik.values.descriptionEn.length}/300</span>
        <div className="tw-invalid-tooltip">{formik.errors.descriptionEn}</div>
      </div>
      {/* Details FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="detailsFi" className="tw-form-label">
          {t("activities.campaigns.create.form.detailsFi")}
          <Tooltip message="activities.campaigns.create.tooltips.detailsFi" />
        </label>
        <textarea
          id="detailsFi"
          name="detailsFi"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.detailsFi}
          className={twClassNames(isValidCLass("detailsFi"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
        />
        <span className="tw-form-counter">{formik.values.detailsFi.length}/300</span>
        <div className="tw-invalid-tooltip">{formik.errors.detailsFi}</div>
      </div>
      {/* Details EN */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="detailsEn" className="tw-form-label">
          {t("activities.campaigns.create.form.detailsEn")}
        </label>
        <textarea
          id="detailsEn"
          name="detailsEn"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.detailsEn}
          className={twClassNames(isValidCLass("detailsEn"), "tw-form-control tw-min-h-[110px] tw-pr-12")}
        />
        <span className="tw-form-counter">{formik.values.detailsEn.length}/300</span>
        <div className="tw-invalid-tooltip">{formik.errors.detailsEn}</div>
      </div>
      {/* Url */}
      <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
        <label htmlFor="url" className="tw-form-label">
          {t("activities.profile.form.url")}
          <span className="tw-form-label-optional">({t("common.validation.optional")})</span>
          <Tooltip message="activities.campaigns.create.tooltips.url" />
        </label>
        <input
          type="text"
          id="url"
          name="url"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.url}
          className={twClassNames(isValidCLass("url"), "tw-form-control")}
          spellCheck="false"
        />
        <div className="tw-invalid-tooltip">{formik.errors.url}</div>
      </div>
      {/* link title */}
      <div className="tw-relative tw-col-span-6 md:tw-col-span-3">
        <label htmlFor="linkTitle" className="tw-form-label">
          {t("activities.profile.form.linkTitle")}
        </label>
        <select
          id="linkTitle"
          name="linkTitle"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.linkTitle}
          className={twClassNames(isValidCLass("linkTitle"), "tw-form-control")}>
          <option value="readmore">{t("activities.profile.form.linkTitleOptions.readmore")}</option>
          <option value="book">{t("activities.profile.form.linkTitleOptions.book")}</option>
          <option value="contact">{t("activities.profile.form.linkTitleOptions.contact")}</option>
        </select>
        <div className="tw-invalid-tooltip">{formik.errors.linkTitle}</div>
      </div>
    </div>
    {/* Buttons */}
    <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
      <button
        type="button"
        onClick={onPrevious}
        className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.previous")}
      </button>
      <button
        type="button"
        onClick={onNext}
        className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.next")}
      </button>
    </div>
  </div>
)

const Form3 = ({ t, formik, isLoading, isValidCLass, onPrevious, onNext }: FormProps) => (
  <div className="tw-flex tw-flex-col tw-h-full">
    {/* Header */}
    <div className="tw-min-w-[300px] tw-w-full tw-mb-4 tw-border-b">
      <div className="tw-relative md:tw-pr-6">
        <h5 className="tw-text-lg tw-mb-4">{t("activities.campaigns.create.form.phase3.name")}</h5>
        <p className="tw-text-sm tw-text-dark-75">{t("activities.campaigns.create.form.phase3.info")}</p>
      </div>
    </div>
    {/* Content */}
    <div className="tw-grid tw-grid-cols md:tw-grid-cols-6 tw-gap-x-2 tw-gap-y-3 md:tw-gap-y-4">
      {/* Title FI */}
      <div className="tw-relative tw-col-span-6">
        <label htmlFor="publishedAt" className="tw-form-label">
          {t("activities.campaigns.create.form.publishedAt")}
        </label>
        <input
          type="datetime-local"
          id="publishedAt"
          name="publishedAt"
          onChange={formik.handleChange}
          onBlur={formik.handleBlur}
          value={formik.values.publishedAt}
          className={twClassNames(isValidCLass("publishedAt"), "tw-form-control")}
        />
        <div className="tw-invalid-tooltip">{formik.errors.publishedAt}</div>
      </div>
    </div>
    {/* Buttons */}
    <div className="tw-flex tw-grow tw-place-content-between tw-items-end tw-pt-12">
      <button
        type="button"
        onClick={onPrevious}
        className="tw-btn tw-btn-outline tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.previous")}
      </button>
      <button
        type="button"
        onClick={onNext}
        className="tw-btn tw-btn-primary tw-w-full tw-max-w-[180px]"
        disabled={isLoading}>
        {t("activities.campaigns.create.button.publish")}
      </button>
    </div>
  </div>
)

const Preview = ({ data, t, phase }: PreviewProps) => {
  const [lng, setPreviewLanguage] = useState("fi")

  return (
    <Emulator lng={lng} setPreviewLanguage={setPreviewLanguage} flex>
      {phase === 1 ? (
        <>
          <div
            className="tw-flex tw-self-center tw-flex-col tw-bg-white tw-border tw-rounded-2xl tw-shadow tw-mx-2.5
              tw-w-[260px]">
            <div className="tw-flex tw-flex-row tw-justify-between tw-p-2.5">
              <span className="tw-text-sm tw-font-bold tw-line-clamp-2">{getLocaleValue(data, "shortTitle", lng)}</span>
              <span className="tw-min-w-[40px] tw-text-right tw-text-xs tw-font-semibold tw-text-dark-50">
                {t("activities.campaigns.create.emulator.published.short", { lng })}
              </span>
            </div>
            <p className="tw-text-sm tw-text-dark-75 tw-line-clamp-3 tw-px-2.5">
              {getLocaleValue(data, "shortDescription", lng)}
            </p>
            <div className="tw-flex tw-self-center tw-border-t tw-w-full">
              <span
                className="tw-flex tw-flex-row tw-items-center tw-justify-center tw-w-full tw-text-sm tw-text-primary
                  tw-p-2.5">
                {t("activities.campaigns.create.emulator.readmore", { lng })}
              </span>
            </div>
          </div>
          {/* Placeholder */}
          <div
            className="tw-h-[140px] tw-w-[15px] tw-bg-[#CDCED0] tw-shadow tw-rounded-tl tw-rounded-bl tw-flex
              tw-self-center"
          />
        </>
      ) : (
        <div
          className="tw-flex tw-self-center tw-flex-col tw-bg-white tw-border tw-rounded-2xl tw-shadow tw-mx-2.5 tw-p-4
            tw-w-full">
          <span className="tw-text-xs tw-text-dark-50">
            {t("activities.campaigns.create.emulator.published.full", { lng })}
          </span>
          <span className="tw-font-semibold tw-text-sm tw-mt-4">{getLocaleValue(data, "title", lng)}</span>
          {getLocaleValue(data, "description", lng) && (
            <span className="tw-text-sm tw-text-dark-75 tw-mt-1.5">{getLocaleValue(data, "description", lng)}</span>
          )}
          {getLocaleValue(data, "details", lng) && (
            <>
              <span className="tw-font-semibold tw-text-sm tw-mb-1.5 tw-mt-4">
                {t("activities.campaigns.create.emulator.details", { lng })}
              </span>
              <span className="tw-text-sm tw-text-dark-75">{getLocaleValue(data, "details", lng)}</span>
            </>
          )}
          {data.url && (
            <button className="tw-w-full tw-btn tw-btn-primary tw-mt-4" type="button">
              {t("activities.campaigns.create.form.linkTitleOptions." + data.linkTitle.toLowerCase(), { lng })}
            </button>
          )}
        </div>
      )}
    </Emulator>
  )
}

const CreateCampaignContainer = () => {
  const { t } = useTranslation()
  const history = useHistory()

  const { selectedActivityId } = useSelectedProject()
  const { data: activity } = useActivity(selectedActivityId)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { data: campaigns = [] } = useCampaigns(selectedActivityId) as { data: any[] }
  const createCampaignMutation = useCreateCampaign()
  const isLoading = createCampaignMutation.isPending

  const [phase, setPhase] = useState(1)
  const [confirmation, setConfirmation] = useState<ConfirmationState>({ show: false, callback: null, title: "", content: "" })

  const hasActiveCampaigns = useMemo(() => {
    return campaigns.some((campaign: { status: string }) => campaign.status !== "expired")
  }, [campaigns])

  const formik = useFormik({
    initialValues: {
      ownerTeamId: activity?.ownerTeamId,
      titleFi: "",
      titleEn: "",
      descriptionFi: "",
      descriptionEn: "",
      shortTitleFi: "",
      shortTitleEn: "",
      shortDescriptionFi: "",
      shortDescriptionEn: "",
      detailsFi: "",
      detailsEn: "",
      categories: [],
      url: "",
      linkTitle: "readmore",
      publishedAt: ISODateNow()
    },
    enableReinitialize: false,
    validateOnBlur: false,
    validationSchema: validationSchema(phase, t),
    onSubmit: values => onSave(values)
  })

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const onSave = (values: any) => {
    if (hasActiveCampaigns) {
      setConfirmation({
        show: true,
        callback: () => history.push("/activities/campaigns"),
        title: "unable",
        content: "campaignExists"
      })
      return
    }

    const data = {
      ...values,
      shortTitle: values.shortTitleFi || values.shortTitleEn,
      shortDescription: values.shortDescriptionFi || values.shortDescriptionEn,
      title: values.titleFi || values.titleEn,
      description: values.descriptionFi || values.descriptionEn,
      details: values.detailsFi || values.detailsEn,
      activityId: selectedActivityId,
      url: withHttp(values.url),
      publishedAt: new Date(values.publishedAt).toISOString(),
      city: activity?.city
    }

    createCampaignMutation
      .mutateAsync(data)
      .then(() => history.push("/activities/campaigns"))
      .catch(() => {})
  }

  const onNext = () => {
    formik.validateForm().then(errors => {
      formik.setErrors(errors)
      Object.keys(errors).forEach(key => formik.setFieldTouched(key, true))
      if (!Object.keys(errors).length) {
        if (phase === PHASES) {
          formik.handleSubmit()
        } else {
          if (phase === 1) {
            // Copy short values to next phase if long values are empty
            if (!formik.values.titleFi && formik.values.shortTitleFi) {
              formik.setFieldValue("titleFi", formik.values.shortTitleFi)
            }
            if (!formik.values.titleEn && formik.values.shortTitleEn) {
              formik.setFieldValue("titleEn", formik.values.shortTitleEn)
            }
            if (!formik.values.descriptionFi && formik.values.shortDescriptionFi) {
              formik.setFieldValue("descriptionFi", formik.values.shortDescriptionFi)
            }
            if (!formik.values.descriptionEn && formik.values.shortDescriptionEn) {
              formik.setFieldValue("descriptionEn", formik.values.shortDescriptionEn)
            }
          }
          setPhase(phase + 1)
        }
      }
    })
  }

  const onPrevious = () => {
    if (phase <= 1) {
      if (formik.dirty) {
        setConfirmation({
          show: true,
          callback: () => history.push("/activities/campaigns"),
          title: "dirty",
          content: "dirty",
          danger: true
        })
      } else {
        history.push("/activities/campaigns")
      }
    } else {
      setPhase(phase - 1)
    }
  }

  const isValidCLass = (field: string) => (formik.touched as Record<string, boolean>)[field] && (formik.errors as Record<string, string>)[field] && "tw-is-invalid"

  return (
    <>
      <Breadcrumb data={breadcrumbs} />
      <h1 className="tw-font-display tw-font-semibold tw-text-4xl tw-mb-6">{t("activities.campaigns.create.title")}</h1>
      <div className="tw-mb-12 tw-w-full">
        <div className="tw-mb-6 tw-shadow tw-bg-white tw-rounded">
          <div className="tw-flex tw-p-6 max-md:tw-flex-col">
            {/* Left side */}
            <form noValidate onSubmit={formik.handleSubmit} className="tw-w-full md:tw-w-1/2 md:tw-pr-6">
              {(() => {
                switch (phase) {
                  case 1:
                    return (
                      <Form1
                        t={t}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  case 2:
                    return (
                      <Form2
                        t={t}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  case 3:
                    return (
                      <Form3
                        t={t}
                        formik={formik}
                        isLoading={isLoading}
                        isValidCLass={isValidCLass}
                        onPrevious={onPrevious}
                        onNext={onNext}
                      />
                    )
                  default:
                    return null
                }
              })()}
            </form>
            {/* Right side */}
            <div className="tw-w-full md:tw-w-1/2 tw-min-w-[360px] max-xs:tw-hidden">
              <Preview data={formik.values} t={t} phase={phase} />
            </div>
          </div>
        </div>
      </div>
      <div className="tw-mb-12 tw-w-full">
        <Progressbar phases={PHASES} current={phase} translationKeys="activities.campaigns.create.progress" />
      </div>
      <ConfirmationModal
        show={confirmation.show}
        title={confirmation.title}
        content={confirmation.content}
        danger={confirmation.danger}
        onOK={confirmation.callback}
        onCancel={() => setConfirmation({ show: false, callback: null, title: "", content: "" })}
      />
    </>
  )
}

export default CreateCampaignContainer
